import React, { useCallback, useEffect, useState } from "react";
import { Gauge, Sparkles, RefreshCw, TrendingUp, AlertCircle, Target } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import CriticalPath from "./CriticalPath";
import StatusPackSchedule from "./StatusPackSchedule";
import SignoffPanel from "./SignoffPanel";
import ModelRisk from "./ModelRisk";

function TopExposureCard({ clientId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/admin/enterprise360/clients/${clientId}/top-exposure`).then(({ data }) => setData(data)).catch(() => {});
  }, [clientId]);
  if (!data) return null;
  const t = data.top;
  return (
    <div data-testid="top-exposure-card" className="lg:col-span-2 border border-[var(--e360-primary)]/40 bg-[var(--e360-primary)]/[0.04] rounded-lg p-4">
      <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[var(--e360-primary)] inline-flex items-center gap-1 mb-2"><Target size={13} /> Biggest exposure right now</div>
      {!t ? (
        <p data-testid="top-exposure-none" className="text-[13px] text-zinc-500">No open decisions or slipping gates are exposing the programme — the critical path is clear.</p>
      ) : (
        <>
          <div className="flex items-start gap-3 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 rounded bg-[var(--e360-primary)] text-white mt-0.5">{t.ref}</span>
            <p data-testid="top-exposure-headline" className="text-[14px] text-zinc-800 font-medium flex-1 min-w-[220px]">{t.headline}</p>
            {t.value_at_stake_label && <span data-testid="top-exposure-value" className="text-[15px] font-semibold text-[var(--e360-primary)] whitespace-nowrap">~{t.value_at_stake_label}</span>}
          </div>
          {data.count > 1 && (
            <div className="mt-3 border-t border-[var(--e360-primary)]/15 pt-2 space-y-1">
              <div className="text-[10px] tracking-[0.14em] uppercase text-zinc-400">{data.count} open exposures · next in line</div>
              {data.ranked.slice(1).map((r, i) => (
                <div key={r.ref} data-testid={`top-exposure-next-${i}`} className="flex items-center justify-between text-[12px] gap-2">
                  <span className="text-zinc-600 truncate"><span className="text-zinc-400">{r.ref}</span> · {r.title}</span>
                  <span className="text-zinc-500 whitespace-nowrap">{r.value_at_stake_label ? `~${r.value_at_stake_label}` : `${r.impacted} impacted`}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const ragCls = (rag) => rag === "green" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
  : rag === "red" ? "text-red-700 bg-red-50 border-red-200"
  : "text-amber-700 bg-amber-50 border-amber-200";
const scoreColor = (rag) => rag === "green" ? "text-emerald-600" : rag === "red" ? "text-red-600" : "text-amber-600";

function Sparkline({ history }) {
  if (!history || history.length < 2) return null;
  const w = 160, h = 40, pad = 4;
  const xs = history.map((_, i) => pad + (i * (w - 2 * pad)) / (history.length - 1));
  const ys = history.map((p) => h - pad - ((p.score / 100) * (h - 2 * pad)));
  const pts = xs.map((x, i) => `${x.toFixed(1)},${ys[i].toFixed(1)}`).join(" ");
  return (
    <svg data-testid="confidence-trend" width={w} height={h} className="overflow-visible">
      <polyline points={pts} fill="none" stroke="var(--e360-primary)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={xs[xs.length - 1]} cy={ys[ys.length - 1]} r="3" fill="var(--e360-primary)" />
    </svg>
  );
}

function ConfidenceCard({ clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/enterprise360/clients/${clientId}/confidence`); setData(data); }
    catch { toast.error("Could not load the confidence index"); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const c = data?.current;
  return (
    <div data-testid="confidence-card" className="border border-zinc-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[var(--e360-primary)] inline-flex items-center gap-1"><Gauge size={13} /> Programme Confidence Index</div>
        <button data-testid="confidence-refresh" onClick={load} className="text-zinc-300 hover:text-zinc-600 p-1" title="Refresh"><RefreshCw size={14} /></button>
      </div>
      {loading ? <div className="text-sm text-zinc-400 py-6 text-center">Loading…</div> : c ? (
        <>
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <div data-testid="confidence-score" className={`text-5xl font-semibold leading-none ${scoreColor(c.rag)}`}>{c.score}<span className="text-xl text-zinc-300">/100</span></div>
              <span data-testid="confidence-band" className={`inline-block mt-2 text-[11px] rounded-full px-2.5 py-0.5 border font-semibold ${ragCls(c.rag)}`}>{c.band}</span>
            </div>
            <div className="flex-1 min-w-[160px] flex justify-end"><Sparkline history={data.history} /></div>
          </div>
          <p data-testid="confidence-verdict" className="text-[12px] text-zinc-600 mt-3">{c.verdict}</p>
          {c.factors.length > 0 && (
            <div className="mt-3 border-t border-zinc-100 pt-2 space-y-1">
              <div className="text-[10px] tracking-[0.14em] uppercase text-zinc-400">What's dragging the score</div>
              {c.factors.map((f, i) => (
                <div key={i} data-testid={`confidence-factor-${i}`} className="flex items-center justify-between text-[12px]">
                  <span className="text-zinc-600"><span className="text-zinc-400">{f.register}</span> · {f.count}× {f.label}</span>
                  <span className="text-red-600 font-medium tabular-nums">−{f.deduction}</span>
                </div>
              ))}
            </div>
          )}
        </>
      ) : <div className="text-sm text-zinc-500 py-6 text-center">No data.</div>}
    </div>
  );
}

function NarrativeCard({ clientId }) {
  const [n, setN] = useState(null);
  const [busy, setBusy] = useState(false);
  const generate = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/enterprise360/clients/${clientId}/steering-narrative`); setN(data); }
    catch { toast.error("Could not generate the narrative"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="narrative-card" className="border border-zinc-200 rounded-lg p-4">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[var(--e360-primary)] inline-flex items-center gap-1"><Sparkles size={13} /> AI steering-committee narrative</div>
        <button data-testid="narrative-generate-btn" onClick={generate} disabled={busy}
          className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.12em] uppercase text-white bg-[var(--e360-primary)] px-3 py-1.5 rounded hover:opacity-90 disabled:opacity-40">
          {busy ? <RefreshCw size={13} className="animate-spin" /> : <Sparkles size={13} />} {n ? "Regenerate" : "Generate"}
        </button>
      </div>
      {!n ? (
        <p className="text-[12px] text-zinc-500">Auto-writes "the story this week" for the board — grounded entirely in the live registers, the confidence index and the £ value at stake. Nothing is invented.</p>
      ) : (
        <div className="space-y-3">
          <div className="flex items-start gap-2 flex-wrap">
            <span className={`mt-0.5 inline-block w-2.5 h-2.5 rounded-full flex-none ${n.status_rag === "green" ? "bg-emerald-500" : n.status_rag === "red" ? "bg-red-500" : "bg-amber-500"}`} />
            <p data-testid="narrative-headline" className="text-[14px] text-zinc-800 font-medium flex-1">{n.headline}</p>
          </div>
          {(n.sections || []).map((s, i) => (
            <div key={i} data-testid={`narrative-section-${i}`}>
              <div className="text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">{s.title}</div>
              <p className="text-[13px] text-zinc-700 mt-0.5">{s.body}</p>
            </div>
          ))}
          {(n.asks || []).length > 0 && (
            <div className="border-t border-zinc-100 pt-2">
              <div className="text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500 inline-flex items-center gap-1 mb-1"><AlertCircle size={12} /> Asks of the board</div>
              <ul className="space-y-1">
                {n.asks.map((a, i) => <li key={i} data-testid={`narrative-ask-${i}`} className="text-[13px] text-zinc-700 flex gap-1.5"><span className="text-[var(--e360-primary)]">→</span>{a}</li>)}
              </ul>
            </div>
          )}
          <div data-testid="narrative-ai-badge" className="text-[10px] text-zinc-400">
            {n.ai_available ? `Generated by ${n.model || "AI"} from the live registers` : "Deterministic summary (AI unavailable) — grounded in the live registers"}
            {n.generated_at && ` · ${new Date(n.generated_at).toLocaleString("en-GB")}`}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProgrammeAssurance({ clientId }) {
  return (
    <div data-testid="programme-assurance">
      <p className="text-[12px] text-zinc-500 max-w-2xl mb-4">
        <TrendingUp size={12} className="inline mr-1" /> One live assurance score rolled up from all four registers, trended over time — and a board-ready narrative auto-written from the same data.
      </p>
      <div className="grid lg:grid-cols-2 gap-4">
        <TopExposureCard clientId={clientId} />
        <ConfidenceCard clientId={clientId} />
        <NarrativeCard clientId={clientId} />
        <CriticalPath clientId={clientId} />
        <SignoffPanel clientId={clientId} />
        <ModelRisk />
        <StatusPackSchedule clientId={clientId} />
      </div>
    </div>
  );
}
